"use client"

import type { CSSProperties, ReactNode } from "react"
import {
  CircleHalf,
  Clock,
  Heartbeat,
  ListChecks,
  PersonSimple,
  Radioactive,
  Scan,
  TestTube,
  UserCircle,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/intl/use-translations"
import type { TestData } from "@/data/tests"
import {
  InfoTimeIcon,
  InfoRadiationIcon,
  InfoContrastIcon,
  InfoHealthAreasIcon,
  InfoPreparationIcon,
  InfoForWhomIcon,
  GreenCheck,
  BodyFigureIcon,
  ImagingIcon,
} from "@/components/ui/icons"

/* ───────────────────── Quick fact icons ───────────────────── */

/** Compact 20px icon set used by the Quick facts table layout.
 *  Mirrors the icon `type` keys in `TestData.quickFacts` (clock,
 *  no-radiation, body-scan, checklist, person, …). Uses the booking-flow
 *  "Info*" icon family so the table matches the product info / booking
 *  experience, falling back to the DS scan/body icons for the two types
 *  that family doesn't cover. */
function QuickFactTableIcon({ type }: { type: string }) {
  const props = { size: 20, className: "shrink-0 text-neutral-900" }
  switch (type) {
    case "clock":
      return <InfoTimeIcon {...props} />
    case "no-radiation":
      return <InfoRadiationIcon {...props} />
    case "contrast":
      return <InfoContrastIcon {...props} />
    case "droplet":
      return <InfoHealthAreasIcon {...props} />
    case "imaging":
      return <ImagingIcon {...props} />
    case "body-scan":
      return <BodyFigureIcon {...props} />
    case "checklist":
      return <InfoPreparationIcon {...props} />
    case "person":
      return <InfoForWhomIcon {...props} />
    default:
      return <InfoTimeIcon {...props} />
  }
}

const HIGHLIGHT_KEYS = [
  "qfHighlight1",
  "qfHighlight2",
  "qfHighlight3",
  "qfHighlight4",
] as const

/**
 * Quick facts — V06 table layout, shared by every test detail page.
 *
 * Left rail: the summary heading taken from the label-less entry in
 * `facts`, plus the page's CTAs. Right column: a single tinted-surface
 * table with one row per labeled fact (icon + sentence-case label on the
 * left, value + descriptive detail on the right).
 *
 * `mri` switches the left rail to the full-body-MRI benefits checklist
 * (short title + green checks, stretched to the table's height) and leads
 * with the value on mobile.
 *
 * `simple` (the membership-first site) swaps the tinted table for plain
 * rows between hairlines, and stretches the left rail so its CTAs line up
 * with the last row (Eugene, 02.10: the table looked dated, and after the
 * busy hero this section should stay quiet).
 */
/** Whether a label is written entirely in capitals ("TIME NEEDED"). */
function isAllCaps(label: string): boolean {
  return (
    label === label.toLocaleUpperCase() && label !== label.toLocaleLowerCase()
  )
}

export function QuickFactsSection({
  facts,
  ctaButtons,
  mri = false,
  simple = false,
}: {
  facts: TestData["quickFacts"]
  ctaButtons: ReactNode
  mri?: boolean
  simple?: boolean
}) {
  const tt = useTranslations("tests")
  if (!facts?.length) return null

  const tableRows = facts.filter((qf) => qf.label)
  const summaryFact = facts.find((qf) => !qf.label)

  return (
    <section className={simple ? "py-12 md:py-16" : "py-16 md:py-20"}>
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div
          className={cn(
            "grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20",
            (mri || simple) && "lg:items-stretch"
          )}
        >
          {/* Left rail */}
          {mri ? (
            <div className="flex flex-col justify-between">
              <div>
                <h2 className="type-display-md text-neutral-900">
                  {tt("qfHighlightsTitle")}
                </h2>
                <ul className="mt-8 flex flex-col gap-4">
                  {HIGHLIGHT_KEYS.map((key) => (
                    <li key={key} className="flex items-center gap-3">
                      <GreenCheck width={20} height={20} className="shrink-0" />
                      <span className="type-body-lg text-neutral-700">
                        {tt(key as never)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {ctaButtons}
            </div>
          ) : (
            <div className={cn("flex flex-col", simple && "justify-between")}>
              {summaryFact?.detail && (
                <h2 className="type-display-sm max-w-[460px] text-neutral-900">
                  {tt(summaryFact.detail as never)}
                </h2>
              )}
              {ctaButtons}
            </div>
          )}
          {/* Right table */}
          {simple ? (
            <QuickFactRows rows={tableRows} />
          ) : (
            <div className="rounded-[16px] bg-neutral-100 px-6 py-6 md:px-10 md:py-8">
              <ul className="flex flex-col gap-2 sm:grid sm:grid-cols-[auto_1fr] sm:items-start sm:gap-x-6 sm:gap-y-6">
                {tableRows.map((qf, i) => {
                  const rawValue = qf.value
                    ? qf.value.startsWith("qf")
                      ? tt(qf.value as never)
                      : qf.value
                    : ""
                  const label = qf.label ? tt(qf.label as never) : ""
                  const unit = qf.unit ? tt(qf.unit as never) : ""
                  const detail = qf.detail ? tt(qf.detail as never) : ""
                  const valuePart = rawValue
                    ? `${rawValue}${unit ? ` ${unit}` : ""}`
                    : ""
                  return (
                    <li
                      key={i}
                      className="grid grid-cols-1 gap-1 py-3 sm:contents"
                    >
                      {/* Full-body MRI only: on mobile the value (data) leads
                        and the icon+label sits below it as a muted caption;
                        `sm:` resets to the shared desktop label-left /
                        value-right row. Other tests keep the original layout
                        on every size. */}
                      <div
                        className={cn(
                          "flex items-center gap-4",
                          mri && "order-2 sm:order-none"
                        )}
                      >
                        <QuickFactTableIcon type={qf.icon} />
                        <span
                          className={cn(
                            "type-body-lg",
                            // A label stored in capitals ("TIME NEEDED") is
                            // sentence-cased here. One stored in its proper
                            // case renders as written: German capitalises
                            // every noun ("Zusätzliche Zeit"), which CSS
                            // can't know.
                            isAllCaps(label) &&
                              "lowercase first-letter:uppercase",
                            mri
                              ? "text-neutral-600 sm:text-neutral-900"
                              : "text-neutral-900"
                          )}
                        >
                          {label}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "type-body-lg text-left",
                          mri
                            ? "order-1 text-neutral-900 sm:order-none sm:text-neutral-600"
                            : "text-neutral-600"
                        )}
                      >
                        {valuePart ? (
                          <>
                            <span className="block">{valuePart}</span>
                            {detail}
                          </>
                        ) : (
                          detail
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

/** The membership-first rows' icons: Phosphor at one size and weight, so
 *  they read a touch heavier than the booking's set. Keys as in
 *  `QuickFactTableIcon`, plus `test-tube` for a blood test's markers; the
 *  heartbeat replaces the tall tube (Eugene, 02.10). */
const ROW_ICONS: Record<string, PhosphorIcon> = {
  clock: Clock,
  droplet: Heartbeat,
  "body-scan": PersonSimple,
  checklist: ListChecks,
  person: UserCircle,
  contrast: CircleHalf,
  imaging: Scan,
  "test-tube": TestTube,
}

/** The slash over the radiation mark: a gap cut out of the mark along the
 *  diagonal, then the line drawn in it, so it reads on any ground. */
const SLASH_GAP: CSSProperties = {
  maskImage:
    "linear-gradient(45deg, #000 calc(50% - 1.6px), transparent calc(50% - 1.6px), transparent calc(50% + 1.6px), #000 calc(50% + 1.6px))",
  WebkitMaskImage:
    "linear-gradient(45deg, #000 calc(50% - 1.6px), transparent calc(50% - 1.6px), transparent calc(50% + 1.6px), #000 calc(50% + 1.6px))",
}

function FactRowIcon({ type }: { type: string }) {
  if (type === "no-radiation") {
    return (
      <span aria-hidden="true" className="relative flex size-6 shrink-0">
        <span style={SLASH_GAP} className="flex">
          <Radioactive size={24} />
        </span>
        <svg
          viewBox="0 0 24 24"
          className="absolute inset-0 size-6"
          fill="none"
        >
          <path
            d="M5 5l14 14"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </svg>
      </span>
    )
  }
  const Icon = ROW_ICONS[type] ?? Clock
  return <Icon aria-hidden="true" size={24} className="shrink-0" />
}

export type FactRow = {
  /** A `ROW_ICONS` key, or `no-radiation`. */
  icon: string
  label: string
  /** The fact, in medium weight. */
  main: string
  /** Its note, in grey on the same line. */
  sub?: string
}

/** The membership-first fact table, shared by every test page there (the
 *  quick facts, the biomarker pages' detail card, the prostate and pelvic
 *  MRI): label on the left, the fact on the right, hairlines between rows
 *  only, the fact in medium weight for rhythm (Eugene, 02.10). Rows share
 *  out any height the left rail adds. */
export function FactRows({ rows }: { rows: FactRow[] }) {
  return (
    <ul className="flex flex-col">
      {rows.map((row) => (
        <li
          key={row.label}
          className="grid flex-1 grid-cols-1 content-center gap-1.5 border-b border-neutral-200 py-3.5 first:pt-0 last:border-b-0 last:pb-0 sm:grid-cols-[minmax(0,15rem)_1fr] sm:gap-x-8"
        >
          <div className="flex items-center gap-3 text-neutral-900">
            <FactRowIcon type={row.icon} />
            <span className="type-body-lg font-medium lowercase first-letter:uppercase [:lang(de)_&]:normal-case">
              {row.label}
            </span>
          </div>
          {/* One line: the fact, then its note in grey (Eugene, 02.10).
              Only a long one, such as preparation, wraps. */}
          <p className="type-body-lg pl-9 sm:pl-0">
            <span className="font-medium text-neutral-900">{row.main}</span>
            {row.sub && (
              <span className="ml-2 text-neutral-500">{row.sub}</span>
            )}
          </p>
        </li>
      ))}
    </ul>
  )
}

/** The quick facts as `FactRows`. */
function QuickFactRows({ rows }: { rows: TestData["quickFacts"] }) {
  const tt = useTranslations("tests")
  return (
    <FactRows
      rows={rows.map((qf) => {
        // A range reads as a minus with a hyphen: 18–80, not 18-80.
        const value = (
          qf.value
            ? qf.value.startsWith("qf")
              ? tt(qf.value as never)
              : qf.value
            : ""
        ).replace(/(\d)-(\d)/g, "$1\u2013$2")
        const unit = qf.unit ? tt(qf.unit as never) : ""
        const detail = qf.detail ? tt(qf.detail as never) : ""
        return {
          // Blood tests count markers under the scan's body figure; a tube
          // fits them better. Their label keys end in `MarkersLabel`.
          icon: qf.label.endsWith("MarkersLabel") ? "test-tube" : qf.icon,
          label: qf.label ? tt(qf.label as never) : "",
          main: value ? `${value}${unit ? ` ${unit}` : ""}` : detail,
          sub: value ? detail : "",
        }
      })}
    />
  )
}
